import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Modal,
  toast,
} from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CalendarIcon,
  DownloadIcon,
  EditIcon,
  FileTextIcon,
  HelpCircle,
  MailIcon,
  RefreshCwIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import type {
  ContractDocumentDTO,
  ContractRenewalDTO,
} from "../../domain/contracts";
import type { ContractDetailResult } from "../../domain/types";
import {
  useContractQuery,
  useDeleteContract,
  useRenewContract,
} from "../api/queries";
import {
  ContractDetailTour,
  useContractDetailTour,
} from "../tour/ContractDetailTour";
import { ContractRenewalForm } from "../ui/ContractRenewalForm";

/** Resolve file key to full API URL for viewing/downloading */
function getDocumentUrl(fileKey: string | null | undefined): string | null {
  if (!fileKey || typeof fileKey !== "string" || fileKey.trim() === "")
    return null;
  return `${config.apiUrl}/files/${fileKey}`;
}

const getStatusBadge = (status: string) => {
  const statusMap: Record<
    string,
    {
      label: string;
      variant: "default" | "destructive" | "secondary" | "outline";
    }
  > = {
    active: { label: "ACTIVE", variant: "default" },
    expiring_soon: { label: "EXPIRING SOON", variant: "secondary" },
    expired: { label: "EXPIRED", variant: "destructive" },
    renewing: { label: "RENEWING", variant: "secondary" },
    terminated: { label: "TERMINATED", variant: "outline" },
    draft: { label: "DRAFT", variant: "outline" },
  };
  const statusInfo = statusMap[status] || {
    label: status.toUpperCase(),
    variant: "outline" as const,
  };
  return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
};

const getDocumentTypeBadge = (type: string) => {
  const typeMap: Record<
    string,
    { label: string; variant: "default" | "secondary" | "outline" }
  > = {
    main: { label: "PRIMARY", variant: "default" },
    attachment: { label: "ATTACHMENT", variant: "outline" },
    renewal: { label: "RENEWAL", variant: "secondary" },
    amendment: { label: "AMENDMENT", variant: "secondary" },
  };
  const typeInfo = typeMap[type] || {
    label: type.toUpperCase(),
    variant: "outline" as const,
  };
  return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
};

function calculateContractHealth(
  startDate: string | Date,
  endDate: string | Date,
): number {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  if (now < start) return 0;
  if (now > end) return 100;
  const total = end - start;
  const elapsed = now - start;
  return Math.round((elapsed / total) * 100);
}

function calculateDaysUntilExpiry(endDate: string | Date): number {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const diff = end - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function isEmploymentTypeName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return (
    n.includes("employment") ||
    n.includes("employee") ||
    n.includes("labor") ||
    n.includes("staff") ||
    n.includes("contract of service") ||
    n.includes("ຈ້າງ") ||
    n.includes("ພະນັກງານ")
  );
}

function getCancellationDeadline(
  endDate: string | Date,
  noticeDays: number,
): Date {
  const end = new Date(endDate);
  end.setDate(end.getDate() - noticeDays);
  return end;
}

/** ທຸກໄຟລ໌ປະເພດ main — ປັດຈຸບັນ / ເວີຊັນສູງ / ອັບໂຫຼດຫຼັງຂຶ້ນກ່ອນ */
function sortMainDocuments(docs: ContractDocumentDTO[]): ContractDocumentDTO[] {
  return docs.slice().sort((a, b) => {
    if (a.isCurrentVersion !== b.isCurrentVersion) {
      return a.isCurrentVersion ? -1 : 1;
    }
    if (a.version !== b.version) return b.version - a.version;
    return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
  });
}

export function ContractDetailPage() {
  const tour = useContractDetailTour();
  const { id } = useParams({ from: "/app/contracts/$id" });
  const nav = useNavigate({ from: "/app/contracts/$id" });
  const contract = useContractQuery(id);
  const deleteContract = useDeleteContract();
  const renewContract = useRenewContract(id);
  const renewModal = useDisclosure();
  const canEdit = useActionPermission(["contracts:update"]);
  const canDelete = useActionPermission(["contracts:delete"]);
  const canRenew = useActionPermission(["contracts:renew"]);

  if (contract.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center p-8">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!contract.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center p-8">
            <div className="text-destructive">ບໍ່ພົບສັນຍາ</div>
          </div>
        </Main>
      </>
    );
  }

  const data = contract.data as ContractDetailResult;
  const contractHealth = calculateContractHealth(data.startDate, data.endDate);
  const daysUntilExpiry = calculateDaysUntilExpiry(data.endDate);
  const cancellationDeadline = getCancellationDeadline(
    data.endDate,
    data.renewalNoticeDays,
  );
  const isEmploymentContract = isEmploymentTypeName(data.typeName);

  const mainDocuments = sortMainDocuments(
    data.documents?.filter(
      (d: ContractDocumentDTO) => d.documentType === "main",
    ) || [],
  );
  const supportingFiles =
    data.documents?.filter(
      (d: ContractDocumentDTO) => d.documentType !== "main",
    ) || [];

  const handleDelete = async () => {
    const ok = await confirm({
      title: "ລຶບສັນຍາ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສັນຍາ "${data.title}"?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (ok) {
      try {
        await deleteContract.mutateAsync(id);
        toast.success("ລຶບສັນຍາສຳເລັດ");
        nav({ to: "/app/contracts" });
      } catch {
        toast.error("ລຶບສັນຍາລົ້ມເຫຼວ");
      }
    }
  };

  const handleRenew = async (vals: {
    newEndDate: Date;
    notes?: string | null;
    renewalDocumentId?: string | null;
  }) => {
    try {
      await renewContract.mutateAsync({
        newEndDate: vals.newEndDate,
        notes: vals.notes ?? undefined,
        renewalDocumentId: vals.renewalDocumentId ?? undefined,
      });
      toast.success("ຕໍ່ອາຍຸສັນຍາສຳເລັດ");
      renewModal.close();
      contract.refetch();
    } catch {
      toast.error("ຕໍ່ອາຍຸສັນຍາລົ້ມເຫຼວ");
    }
  };

  const handleExport = () => {
    nav({ to: "/app/contracts/$id/pdf-preview", params: { id } });
  };

  return (
    <>
      <Header />
      <Main>
        <ContractDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Contract Header */}
        <div className="mb-6">
          <div className="mb-4 flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <h1 className="font-bold text-3xl">{data.title}</h1>
                {getStatusBadge(data.status)}
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <div className="flex items-center gap-4 text-muted-foreground text-sm">
                <span>ອັບເດດລ່າສຸດ: {formatDateLocal(data.updatedAt)}</span>
                {data.typeName && (
                  <span className="flex items-center gap-1">
                    <UsersIcon className="h-4 w-4" />
                    {data.typeName}
                  </span>
                )}
              </div>
            </div>
            <div
              className="flex items-center gap-2"
              data-tourid="detail-header"
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.history.back()}
              >
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບ
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    nav({ to: "/app/contracts/$id/edit", params: { id } })
                  }
                >
                  <EditIcon className="h-4 w-4" />
                  ແກ້ໄຂ
                </Button>
              )}
              {canRenew && (
                <Button variant="default" size="sm" onClick={renewModal.open}>
                  <RefreshCwIcon className="h-4 w-4" />
                  ຕໍ່ອາຍຸ
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleExport}>
                <DownloadIcon className="h-4 w-4" />
                ສົ່ງອອກ
              </Button>
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column */}
          <div className="space-y-6 lg:col-span-2">
            {/* Top Row: General Info, Schedule, Administration */}
            <div className="grid gap-6 md:grid-cols-3">
              {/* GENERAL INFORMATION */}
              <Card data-tourid="detail-basic-info">
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ຂໍ້ມູນທົ່ວໄປ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ເລກທີສັນຍາ
                    </div>
                    <div className="font-medium">
                      {data.contractNumber || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ປະເພດສັນຍາ
                    </div>
                    <div className="mt-1">
                      <Badge variant="default">{data.typeName || "-"}</Badge>
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ຄູ່ສັນຍາ
                    </div>
                    <div className="font-medium">{data.counterparty}</div>
                  </div>
                  {data.counterpartyContact && (
                    <div>
                      <div className="text-muted-foreground text-xs uppercase">
                        ຂໍ້ມູນຕິດຕໍ່
                      </div>
                      <a
                        href={`mailto:${data.counterpartyContact}`}
                        className="text-primary hover:underline"
                      >
                        {data.counterpartyContact}
                      </a>
                    </div>
                  )}
                  {"quotationDocumentId" in data &&
                    data.quotationDocumentId && (
                      <div>
                        <div className="text-muted-foreground text-xs uppercase">
                          ອ້າງອີງເອກະສານລາຍລະອຽດ
                        </div>
                        <Link
                          to="/app/documents/$id"
                          params={{ id: data.quotationDocumentId }}
                          className="text-primary hover:underline"
                        >
                          {"quotationDocumentNumber" in data &&
                          data.quotationDocumentNumber
                            ? data.quotationDocumentNumber
                            : data.quotationDocumentId}
                        </Link>
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* SCHEDULE & TERMS */}
              <Card data-tourid="detail-schedule">
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ລະຍະເວລາ & ເງື່ອນໄຂ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ວັນທີເລີ່ມ
                    </div>
                    <div className="font-medium">
                      {formatDateLocal(data.startDate)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ວັນທີຫມົດອາຍຸ
                    </div>
                    <div className="font-medium">
                      {formatDateLocal(data.endDate)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ລະຍະເວລາ
                    </div>
                    <div className="font-medium">
                      {data.durationDays
                        ? `${Math.round(data.durationDays / 30)} ເດືອນ`
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ຕໍ່ອາຍຸອັດຕະໂນມັດ
                    </div>
                    <div className="mt-1">
                      <Badge variant={data.autoRenewal ? "default" : "outline"}>
                        {data.autoRenewal ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs uppercase">
                      ໄລຍະເວລາແຈ້ງເຕືອນ
                    </div>
                    <div className="font-medium">
                      {data.renewalNoticeDays} ວັນກ່ອນຫມົດອາຍຸ
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ADMINISTRATION */}
              <Card data-tourid="detail-administration">
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ການບໍລິຫານ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {isEmploymentContract && data.employeeName && (
                    <div>
                      <div className="text-muted-foreground text-xs uppercase">
                        ພະນັກງານທີ່ຜູກກັບສັນຍາ
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                          <UserIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="font-medium">{data.employeeName}</div>
                      </div>
                    </div>
                  )}
                  {data.responsiblePersonName && (
                    <div>
                      <div className="text-muted-foreground text-xs uppercase">
                        ຜູ້ຮັບຜິດຊອບ
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                          <UserIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">
                            {data.responsiblePersonName}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {data.departmentName && (
                    <div>
                      <div className="text-muted-foreground text-xs uppercase">
                        ພະແນກ
                      </div>
                      <div className="font-medium">{data.departmentName}</div>
                    </div>
                  )}
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground uppercase">
                        ສຸຂະພາບສັນຍາ
                      </span>
                      <span className="font-medium">{contractHealth}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${contractHealth}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Critical Cancellation Deadline Alert */}
            {data.autoRenewal &&
              daysUntilExpiry > 0 &&
              daysUntilExpiry <= data.renewalNoticeDays && (
                <div className="rounded-lg border border-orange-500 bg-orange-50 p-4 dark:bg-orange-950">
                  <div className="flex items-start gap-3">
                    <CalendarIcon className="mt-0.5 h-4 w-4 text-orange-600" />
                    <div className="flex-1">
                      <div className="mb-1 font-semibold text-orange-900 dark:text-orange-100">
                        ກຳນົດເວລາຍົກເລີກທີ່ສຳຄັນ
                      </div>
                      <div className="text-orange-800 text-sm dark:text-orange-200">
                        ສັນຍານີ້ຕ້ອງການການແຈ້ງເຕືອນການຍົກເລີກເປັນເວລາ{" "}
                        <strong>{data.renewalNoticeDays} ວັນ</strong>.
                        ເພື່ອປ້ອງກັນການຕໍ່ອາຍຸອັດຕະໂນມັດ,
                        ການແຈ້ງເຕືອນຕ້ອງຖືກສົ່ງຢ່າງເປັນທາງການບໍ່ຊ້ຳກວ່າ{" "}
                        <strong>{formatDateLocal(cancellationDeadline)}</strong>
                        .
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* SPECIAL CONDITIONS */}
            {data.specialConditions && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ເງື່ອນໄຂພິເສດ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <blockquote className="border-primary border-l-4 pl-4 text-muted-foreground italic">
                    {data.specialConditions}
                  </blockquote>
                </CardContent>
              </Card>
            )}

            {/* Primary documents (ທຸກໄຟລ໌ປະເພດ main) */}
            {mainDocuments.length > 0 && (
              <Card data-tourid="detail-documents">
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ເອກະສານຫຼັກ
                    {mainDocuments.length > 1 ? (
                      <span className="ml-2 font-normal text-muted-foreground">
                        ({mainDocuments.length})
                      </span>
                    ) : null}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {mainDocuments.map(
                    (doc: ContractDocumentDTO, index: number) => (
                      <div
                        key={doc.id}
                        className={
                          index > 0 ? "space-y-4 border-t pt-6" : "space-y-4"
                        }
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">
                            {doc.title?.trim() ||
                              doc.fileKey?.split("/").pop() ||
                              "—"}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            v{doc.version}
                          </Badge>
                          {doc.isCurrentVersion ? (
                            <Badge variant="secondary" className="text-xs">
                              ປັດຈຸບັນ
                            </Badge>
                          ) : null}
                        </div>
                        {doc.notes ? (
                          <p className="text-muted-foreground text-sm">
                            {doc.notes}
                          </p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
                          <UserIcon className="h-3 w-3" />
                          {doc.uploadedByName || "-"}
                          <span>•</span>
                          <span>{formatDateTimeLocal(doc.uploadedAt)}</span>
                        </div>
                        {index === 0 && getDocumentUrl(doc.fileKey) ? (
                          <div className="aspect-3/4 max-h-[min(70vh,720px)] w-full overflow-hidden rounded-lg border bg-muted">
                            <iframe
                              src={getDocumentUrl(doc.fileKey) || ""}
                              className="h-full w-full"
                              title={doc.title}
                            />
                          </div>
                        ) : index > 0 && getDocumentUrl(doc.fileKey) ? (
                          <p className="text-muted-foreground text-sm">
                            ໃຊ້ປຸ່ມ ເບິ່ງ ເພື່ອເປີດຕົວຢ່າງໃນແທັບໃໝ່ (ຫຼືສະແດງໄຟລ໌ທີ່ 1 ດ້ານເທິງ)
                          </p>
                        ) : (
                          <div className="flex aspect-3/4 max-h-48 w-full items-center justify-center rounded-lg border bg-muted">
                            <FileTextIcon className="h-16 w-16 text-muted-foreground" />
                          </div>
                        )}
                        <div className="text-muted-foreground text-xs">
                          {doc.fileKey?.split("/").pop() ||
                            formatDateLocal(doc.uploadedAt)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {getDocumentUrl(doc.fileKey) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  window.open(
                                    getDocumentUrl(doc.fileKey) || "",
                                    "_blank",
                                  )
                                }
                              >
                                <FileTextIcon className="h-4 w-4" />
                                ເບິ່ງ
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  const url = getDocumentUrl(doc.fileKey);
                                  if (!url) return;
                                  const link = document.createElement("a");
                                  link.href = url;
                                  link.download =
                                    doc.fileKey?.split("/").pop() || doc.title;
                                  link.click();
                                }}
                              >
                                <DownloadIcon className="h-4 w-4" />
                                ດາວໂຫຼດ
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* SUPPORTING FILES */}
            {supportingFiles.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                      ໄຟລ໌ສະໜັບສະໜູນ
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <FileTextIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {supportingFiles.map((file: ContractDocumentDTO) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between rounded-lg border p-2"
                      >
                        <div className="flex items-center gap-2">
                          <FileTextIcon className="h-4 w-4 text-muted-foreground" />
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {file.fileKey?.split("/").pop() || file.title}
                            </div>
                            <div className="mt-1">
                              {getDocumentTypeBadge(file.documentType)}
                            </div>
                          </div>
                        </div>
                        {getDocumentUrl(file.fileKey) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              window.open(
                                getDocumentUrl(file.fileKey) || "",
                                "_blank",
                              )
                            }
                          >
                            <DownloadIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* COUNTERPARTY CONTACTS */}
            {data.counterpartyContact && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ຕິດຕໍ່ຄູ່ສັນຍາ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 rounded-lg border p-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <UserIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{data.counterparty}</div>
                        <div className="text-muted-foreground text-sm">
                          {data.counterpartyContact}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MailIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* WARNINGS */}
            {data.warnings && (
              <Card
                className="border-orange-200 bg-orange-50 dark:bg-orange-950"
                data-tourid="detail-notes"
              >
                <CardHeader>
                  <CardTitle className="font-semibold text-orange-900 text-sm uppercase tracking-wide dark:text-orange-100">
                    ຂໍ້ຄວນລະວັງ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-orange-800 text-sm dark:text-orange-200">
                    {data.warnings}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* NOTES */}
            {data.notes && (
              <Card data-tourid="detail-notes">
                <CardHeader>
                  <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                    ຫມາຍເຫດສຳຄັນ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-muted-foreground text-sm">
                    {data.notes}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Contract Lifecycle & Renewal Roadmap */}
        {data.renewals && data.renewals.length > 0 && (
          <Card className="mt-6" data-tourid="detail-renewals">
            <CardHeader>
              <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                ວົງຈອນຊີວິດສັນຍາ & ແຜນທາງການຕໍ່ອາຍຸ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className="flex items-center gap-4 overflow-x-auto pb-4">
                  {/* Initial Term */}
                  <div className="flex min-w-[150px] flex-col items-center gap-2 rounded-lg border bg-muted/50 p-3">
                    <div className="text-muted-foreground text-xs">
                      ໄລຍະເວລາເບື່ອຕົ້ນ
                    </div>
                    <div className="text-center font-medium text-sm">
                      {formatDateLocal(data.startDate)}
                    </div>
                  </div>

                  {/* Renewals */}
                  {data.renewals.map(
                    (renewal: ContractRenewalDTO, index: number) => (
                      <div
                        key={renewal.id}
                        className="flex min-w-[150px] flex-col items-center gap-2"
                      >
                        <div className="h-0.5 w-full bg-primary" />
                        <div className="flex min-w-[150px] flex-col items-center gap-2 rounded-lg border bg-primary/10 p-3">
                          <div className="text-muted-foreground text-xs">
                            ຕໍ່ອາຍຸ {index + 1}
                          </div>
                          <div className="text-center font-medium text-sm">
                            {formatDateLocal(renewal.previousEndDate)} -{" "}
                            {formatDateLocal(renewal.newEndDate)}
                          </div>
                        </div>
                      </div>
                    ),
                  )}

                  {/* Current Term */}
                  <div className="flex min-w-[150px] flex-col items-center gap-2 rounded-lg border-2 border-primary bg-primary/10 p-3">
                    <div className="flex items-center gap-1 text-primary text-xs">
                      <RefreshCwIcon className="h-3 w-3" />
                      ໄລຍະເວລາປັດຈຸບັນ
                    </div>
                    <div className="text-center font-medium text-sm">
                      {formatDateLocal(data.startDate)} -{" "}
                      {formatDateLocal(data.endDate)}
                    </div>
                  </div>

                  {/* Upcoming */}
                  {data.autoRenewal && (
                    <div className="flex min-w-[150px] flex-col items-center gap-2 rounded-lg border border-dashed p-3">
                      <div className="text-muted-foreground text-xs">
                        ຈະມາເຖິງ
                      </div>
                      <div className="text-center font-medium text-sm">
                        {formatDateLocal(data.endDate)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renewal History */}
        {data.renewals && data.renewals.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="font-semibold text-sm uppercase tracking-wide">
                ປະຫວັດການຕໍ່ອາຍຸ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.renewals.map((renewal: ContractRenewalDTO) => (
                  <div key={renewal.id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium">
                          ຈາກ {formatDateLocal(renewal.previousEndDate)} ເປັນ{" "}
                          {formatDateLocal(renewal.newEndDate)}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          ຕໍ່ອາຍຸເມື່ອ {formatDateTimeLocal(renewal.renewalDate)} ໂດຍ{" "}
                          {renewal.renewedByName || "-"}
                        </div>
                        {renewal.notes && (
                          <div className="mt-2 text-muted-foreground text-sm">
                            {renewal.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </Main>

      {/* Renew Modal */}
      <Modal
        open={renewModal.isOpen}
        onOpenChange={renewModal.toggle}
        title="ຕໍ່ອາຍຸສັນຍາ"
        size="lg"
      >
        <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
          <ContractRenewalForm
            initialEndDate={data.endDate}
            contractTitle={data.title}
            onSubmit={handleRenew}
            submitting={renewContract.isPending}
            onCancel={() => renewModal.close()}
          />
        </div>
      </Modal>
    </>
  );
}
